import { Box, Button, Chip, FormControl, InputLabel, MenuItem, Select as MuiSelect,Stack, Typography } from '@mui/material';
import get from 'lodash-es/get';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router-dom';

import { API_SHARED_APP_LIST_CREATE_URL, API_SHARED_APP_LIST_EDIT_URL } from '../../constants/apiUrls';
import { SHARED_APP_LIST_EDIT_URL as SHARED_APP_LIST_EDIT_ROUTE_URL, SHARED_APP_LIST_URL } from '../../constants/routeUrls';
import { useConfig } from '../../hooks/useConfig';
import useConfirmNavChange from '../../hooks/useConfirmNavChange';
import { LabelEntry,LabelType, useLabelEntries, useLabelTypes } from '../../hooks/useLabels';
import { useSnackBar } from '../../hooks/useSnackBar';
import { camelCaseKeys, snakeCaseKeys } from '../../util/object';
import { getRequestJSON, postRequestJSON, putRequestJSON } from '../../util/services';
import Select from '../forms/Select';
import TextField from '../forms/TextField';
import AppListContentsTable from './AppListContentsTable';

interface SharedAppFormProps {
  id?: string;
}

interface SharedAppFormInputs {
  id?: number;
  name: string;
  description: string;
  listType: string;
  labelEntryIds?: number[];
}

const defaultValues: SharedAppFormInputs = {
  name: '',
  description: '',
  listType: '',
  labelEntryIds: []
};

const normalizePayload = (data: SharedAppFormInputs) => snakeCaseKeys(data);

function SharedAppForm({ id }: SharedAppFormProps) {
  const { apiHost } = useConfig();
  const { showSnackBar } = useSnackBar();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  // State for label management
  const [selectedLabelType, setSelectedLabelType] = useState<string>('');
  const [selectedLabelEntryId, setSelectedLabelEntryId] = useState<number | null>(null);
  const [selectedLabels, setSelectedLabels] = useState<Array<{id: number, type: string, entry: string}>>([]);

  // Fetch label data
  const { data: labelTypesData } = useLabelTypes();
  const { data: labelEntriesData } = useLabelEntries();

  const form = useForm<SharedAppFormInputs>({
    defaultValues
  });

  const {
    handleSubmit,
    formState: { isDirty },
    reset,
    setValue
  } = form;
  
  useConfirmNavChange(() => isDirty);

  // Effect to synchronize selectedLabels with form values
  useEffect(() => {
    setValue('labelEntryIds', selectedLabels.map(label => label.id), { 
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    });
  }, [selectedLabels, setValue]);

  // Filter label entries based on selected label type
  const filteredLabelEntries = useMemo(() => {
    if (!selectedLabelType || !labelEntriesData?.collection) return [];
    
    // Find the label type ID
    const labelType = labelTypesData?.collection?.find(
      (type: LabelType) => type.labelType.toLowerCase() === selectedLabelType.toLowerCase()
    );
    
    if (!labelType) return [];
    
    // Filter entries by label type ID
    return labelEntriesData.collection
      .filter((entry: LabelEntry) => entry.labelTypeId === labelType.id);
  }, [selectedLabelType, labelEntriesData, labelTypesData]);

  // Handle label selection and management
  const handleLabelTypeChange = (value: string) => {
    setSelectedLabelType(value);
    setSelectedLabelEntryId(null);
  };

  const handleLabelEntryChange = (value: number | null) => {
    setSelectedLabelEntryId(value);
  };

  const handleAddLabel = () => {
    if (!selectedLabelEntryId) return;

    // Find the selected entry
    const selectedEntry = labelEntriesData?.collection?.find(
      (entry: LabelEntry) => entry.id === selectedLabelEntryId
    );

    if (!selectedEntry) return;

    // Check if label is already selected
    const isAlreadySelected = selectedLabels.some(label => label.id === selectedEntry.id);
    if (isAlreadySelected) {
      showSnackBar('This label is already selected', 'info', {
        event: 'shared_app_list_label_already_selected',
        label_entry_id: selectedLabelEntryId,
      });
      return;
    }

    // Add to selected labels
    const newLabel = {
      id: selectedEntry.id,
      type: selectedEntry.labelTypeName,
      entry: selectedEntry.entry
    };

    const updatedLabels = [...selectedLabels, newLabel];
    setSelectedLabels(updatedLabels);

    // Reset selections
    setSelectedLabelEntryId(null);
  };

  const handleRemoveLabel = (labelId: number) => {
    const updatedLabels = selectedLabels.filter(label => label.id !== labelId);
    setSelectedLabels(updatedLabels);
  };

  useEffect(() => {
    const fetchSharedAppList = async () => {
      if (!isEdit) return;

      const response = await getRequestJSON(
        apiHost,
        generatePath(API_SHARED_APP_LIST_EDIT_URL, { id })
      );

      if (response && response.model) {
        const data = camelCaseKeys(response.model);

        // Set form values from API response
        reset({
          id: data.id,
          name: data.name,
          description: data.description,
          listType: data.listType,
          labelEntryIds: data.labels?.map(label => label.id) || []
        });

        // Set selected labels from API response
        if (data.labels && data.labels.length > 0) {
          setSelectedLabels(data.labels.map(label => ({
            id: label.id,
            type: label.type,
            entry: label.entry
          })));
        }
      } else {
        showSnackBar('Failed to load shared app list', 'error', {
          event: 'shared_app_list_load_error',
          list_id: id,
        });
      }
    };

    fetchSharedAppList();
  }, [apiHost, id, isEdit, reset, showSnackBar, form]);

  const onSubmit = async (data: SharedAppFormInputs) => {
    const payload = normalizePayload(data);
    let response;

    try {
      if (isEdit) {
        response = await putRequestJSON(
          apiHost,
          generatePath(API_SHARED_APP_LIST_EDIT_URL, { id }),
          payload
        );
      } else {
        response = await postRequestJSON(
          apiHost,
          API_SHARED_APP_LIST_CREATE_URL,
          {
            shared_app_list: payload
          }
        );
      }

      const { errors: responseErrors } = response;

      if (responseErrors) {
        const messages = Array.isArray(responseErrors) ? responseErrors : [responseErrors];
        messages.forEach((message) => {
          showSnackBar(message, 'error', { event: 'shared_app_list_save_error' });
        });
        return;
      }

      if (!isEdit) {
        const responseData = get(response, 'model') || response;
        const newListId = responseData?.id;
        showSnackBar('Successfully saved shared app list', 'success', {
          event: 'shared_app_list_created',
          list_id: newListId,
        });
        
        if (newListId) {
          reset(defaultValues);
          // Wait 1 sec so the form has time to reset the isDirty flag so it will not ask to confirm changes.
          setTimeout(() => {
            const editUrl = generatePath(SHARED_APP_LIST_EDIT_ROUTE_URL, { id: newListId.toString() });
            navigate(editUrl, { replace: true });
          }, 1000);
        } else {
          reset(defaultValues);
        }
      } else {
        const responseData = get(response, 'model') || response;
        const formData = camelCaseKeys(responseData);
        showSnackBar('Successfully saved shared app list', 'success', {
          event: 'shared_app_list_updated',
          list_id: id,
        });
        reset(formData);
      }

    } catch (error) {
      showSnackBar(`Error saving shared app list: ${error.message}`, 'error', {
        event: 'shared_app_list_save_error',
      });
    }
  };

  return (
    <Stack spacing={3} p={2}>
      <Typography variant="h5">
        {isEdit ? 'Edit Shared App List' : 'New Shared App List'}
      </Typography>
      
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={3} maxWidth={600}>
            <TextField
              name="name"
              label="Name"
              rules={{ required: 'Name is required' }}
              fullWidth
            />
            
            <TextField
              name="description"
              label="Description"
              fullWidth
              multiline
              rows={3}
            />
            
            <Select
              name="listType"
              label="List Type"
              rules={{ required: 'Type is required' }}
              fullWidth
              options={[
                { value: '', label: 'Select List Type' },
                { value: 'bundle_id', label: 'bundle_id' },
                { value: 'app_ad_space_id', label: 'app_ad_space_id' }
              ]}
            />

            {/* Label Management Section */}
            <Box sx={{ mt: 2 }}>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                {/* Label Type dropdown */}
                <FormControl sx={{ minWidth: 200 }} size="small">
                  <InputLabel id="label-type-select-label">Label Type</InputLabel>
                  <MuiSelect
                    labelId="label-type-select-label"
                    value={selectedLabelType}
                    label="Label Type"
                    onChange={(e) => handleLabelTypeChange(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Select a label type</em>
                    </MenuItem>
                    {labelTypesData?.collection?.map((type: LabelType) => (
                      <MenuItem key={type.id} value={type.labelType}>
                        {type.labelType}
                      </MenuItem>
                    ))}
                  </MuiSelect>
                </FormControl>

                {/* Label Entry dropdown */}
                <FormControl sx={{ minWidth: 200 }} size="small">
                  <InputLabel id="label-entry-select-label">Label Entry</InputLabel>
                  <MuiSelect
                    labelId="label-entry-select-label"
                    value={selectedLabelEntryId || ''}
                    label="Label Entry"
                    onChange={(e) => handleLabelEntryChange(e.target.value as number)}
                    disabled={!selectedLabelType || filteredLabelEntries.length === 0}
                  >
                    <MenuItem value="">
                      <em>Select a label entry</em>
                    </MenuItem>
                    {filteredLabelEntries.map((entry: LabelEntry) => (
                      <MenuItem key={entry.id} value={entry.id}>
                        {entry.entry}
                      </MenuItem>
                    ))}
                  </MuiSelect>
                </FormControl>

                {/* Add Label button */}
                <Button 
                  variant="outlined"
                  onClick={handleAddLabel}
                  disabled={!selectedLabelEntryId}
                >
                  Add Label
                </Button>
              </Stack>

              {/* Selected Labels display */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                {selectedLabels.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No labels selected
                  </Typography>
                ) : (
                  selectedLabels.map((label) => (
                    <Chip
                      key={label.id}
                      label={`${label.type}: ${label.entry}`}
                      onDelete={() => handleRemoveLabel(label.id)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))
                )}
              </Box>
            </Box>
            
            <Stack direction="row" spacing={2} justifyContent="flex-end" mt={2}>
              <Button 
                variant="outlined" 
                onClick={() => navigate(SHARED_APP_LIST_URL)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                disabled={!isDirty}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        </form>
      </FormProvider>
      
      {/* App List Contents Table - outside the form */}
      {isEdit && id && (
        <AppListContentsTable sharedAppListId={id} />
      )}
    </Stack>
  );
}

export default SharedAppForm; 